import { LoaderFunction } from 'react-router-dom';
import { AuthContextType } from '../../../../hooks/useAuth';
import { store } from '../../../../state/configureStore';

// Types
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';

// GQL
import { client } from '../../../../gql/client';
import { selectAllOrders } from '../../../../gql/selectors/orders';
import { GET_CAMPAIGNS } from '../../../../gql/queries/campaign';
import { refreshOrderFlags } from '../../../../gql/services/orderFlags';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { FETCH_TEAMS, FETCH_USERS } from '../../../../gql/queries/users';
import { FETCH_PREDEFINED_ORDERS } from '../../../../gql/queries/orders/predefinedData';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../state/actions/ui/cursorTables';

export const LoaderCampaigns: (
  userData: AuthContextType | null,
  tableId?: string
) => LoaderFunction =
  (userData, tableId = tableIds.CAMPAIGNS) =>
  async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
  void refreshOrderFlags(userData);
  try {
  if (userData?.user) {
    const cursorCampaign = getCursor(tableId)(store.getState());
    const dispatch = store.dispatch;
    const roleActive = userData.user.user.rolActive;

    // Fix A4: Pre-set concessionId when there's only 1 concession to avoid Apollo cache partition splits
    let effectiveConcessionId = cursorCampaign.concessionId;
    if (!effectiveConcessionId) {
      try {
        const { data: { concessions } } = await client.query({ query: GET_CONCESSIONS });
        if (concessions?.length === 1) {
          effectiveConcessionId = concessions[0].id;
          dispatch(setCursor(tableId, { ...cursorCampaign, concessionId: effectiveConcessionId }));
        }
      } catch { /* continue without concessionId */ }
    }

    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido.
    dispatch(loadingData(tableId, false));

    const readCampaigns = client.readQuery({
      query: GET_CAMPAIGNS,
      variables: {
        input: {
          take: cursorCampaign.take,
          cursor: cursorCampaign.cursor,
          orderBy: cursorCampaign.orderBy,
          sortField: cursorCampaign.sortField,
          name: cursorCampaign.name,
          fromDate: cursorCampaign.fromDate,
          toDate: cursorCampaign.toDate,
          concessionId: effectiveConcessionId,
          predefinedOrderId: cursorCampaign.predefinedOrderId,
          states: cursorCampaign.states,
          userIds: cursorCampaign.userIds,
          pkInitKm: cursorCampaign.pkInitKm,
          pkInitMeter: cursorCampaign.pkInitMeter,
          pkEndKm: cursorCampaign.pkEndKm,
          pkEndMeter: cursorCampaign.pkEndMeter,
          description: cursorCampaign.description,
          teamIds: cursorCampaign.teamIds ?? [],
          track: cursorCampaign.track,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: cursorCampaign.onlyFlagged,
        },
      },
    });

    const pageInfo = readCampaigns?.campaigns?.pageInfo;
    const totalCount = readCampaigns?.campaigns?.totalCount;
    const isCacheEmpty = JSON.stringify(readCampaigns) === '{}';
    const campaigns =
      readCampaigns &&
      !isCacheEmpty &&
      (await selectAllOrders(readCampaigns?.campaigns?.campaigns));

    const [
      { data: filters },
      { data: teamsData },
      { data: predefinedData },
      { data: usersData },
    ] = await Promise.all([
      client.query({ query: GET_CONCESSIONS }),
      client.query({ query: FETCH_TEAMS }),
      client.query({ query: FETCH_PREDEFINED_ORDERS }),
      client.query({ query: FETCH_USERS, fetchPolicy: 'cache-first' }),
    ]);

    const filtersData = { data: { predefinedOrders: predefinedData?.predefinedOrders } };

    const combinedData = {
      totalCount,
      campaigns,
      filters: { ...filters, teams: teamsData?.teams, users: usersData?.users },
      filtersData,
      pageInfo,
      roleActive,
    };

    if (!readCampaigns || isCacheEmpty) {
      dispatch(loadingData(tableId, true));
      try {
      let data;
      if (isCacheEmpty) {
        // Tarea #11: respetar el take guardado en cursor (page-size selector).
        const effectiveTake = firstPageTake(cursorCampaign.take, takeBase);
        data = await client.query({
          query: GET_CAMPAIGNS,
          variables: {
            input: {
              take: effectiveTake,
              cursor: '',
              // Va aunque sea `undefined` (entonces es un no-op: una clave
              // undefined no entra en la keyArgs de Apollo). Omitirlo partía la
              // caché al ordenar con la caché fría.
              sortField: cursorCampaign.sortField,
              orderBy: cursorCampaign.orderBy ?? 'desc',
              name: cursorCampaign.name,
              fromDate: cursorCampaign.fromDate,
              toDate: cursorCampaign.toDate,
              concessionId: effectiveConcessionId,
              predefinedOrderId: cursorCampaign.predefinedOrderId,
              states: cursorCampaign.states,
              userIds: cursorCampaign.userIds,
              pkInitKm: cursorCampaign.pkInitKm,
              pkInitMeter: cursorCampaign.pkInitMeter,
              pkEndKm: cursorCampaign.pkEndKm,
              pkEndMeter: cursorCampaign.pkEndMeter,
              description: cursorCampaign.description,
              teamIds: cursorCampaign.teamIds ?? [],
              track: cursorCampaign.track,
              onlyFlagged: cursorCampaign.onlyFlagged,
            },
          },
        });

        // OMIT sortField + orderBy en setCursorData: cursorTables merge los preserva del cursor activo
        const setCursorData = {
          take: effectiveTake,
          cursor: '',
          name: cursorCampaign.name,
          fromDate: cursorCampaign.fromDate,
          toDate: cursorCampaign.toDate,
          concessionId: effectiveConcessionId,
          predefinedOrderId: cursorCampaign.predefinedOrderId,
          states: cursorCampaign.states,
          userIds: cursorCampaign.userIds,
          pkInitKm: cursorCampaign.pkInitKm,
          pkInitMeter: cursorCampaign.pkInitMeter,
          pkEndKm: cursorCampaign.pkEndKm,
          pkEndMeter: cursorCampaign.pkEndMeter,
          description: cursorCampaign.description,
          teamIds: cursorCampaign.teamIds ?? [],
          track: cursorCampaign.track,
          onlyFlagged: cursorCampaign.onlyFlagged,
        };

        dispatch(setCursor(tableId, setCursorData));
      } else {
        data = await client.query({
          query: GET_CAMPAIGNS,
          variables: {
            input: {
              take: cursorCampaign.take,
              cursor: cursorCampaign.cursor,
              orderBy: cursorCampaign.orderBy,
              sortField: cursorCampaign.sortField,
              name: cursorCampaign.name,
              fromDate: cursorCampaign.fromDate,
              toDate: cursorCampaign.toDate,
              concessionId: effectiveConcessionId,
              predefinedOrderId: cursorCampaign.predefinedOrderId,
              states: cursorCampaign.states,
              userIds: cursorCampaign.userIds,
              pkInitKm: cursorCampaign.pkInitKm,
              pkInitMeter: cursorCampaign.pkInitMeter,
              pkEndKm: cursorCampaign.pkEndKm,
              pkEndMeter: cursorCampaign.pkEndMeter,
              description: cursorCampaign.description,
              teamIds: cursorCampaign.teamIds ?? [],
              track: cursorCampaign.track,
              onlyFlagged: cursorCampaign.onlyFlagged,
            },
          },
        });
      }

      const pageInfo = data?.data.campaigns.pageInfo;
      const totalCount = data?.data.campaigns.totalCount;
      const campaigns = await selectAllOrders(data?.data.campaigns.campaigns);

      const combinedData = {
        totalCount,
        campaigns,
        filters: { ...filters, teams: teamsData?.teams, users: usersData?.users },
        filtersData,
        pageInfo,
        roleActive,
      };
      return combinedData;
      } finally {
        // FIX (loader-loading-flag-leak): cierra el flag aunque MISS lance.
        dispatch(loadingData(tableId, false));
      }
    }

    return combinedData;
  }
  return {};
  } catch (err) {
    console.log('Loader offline fallback:', err);
    // FIX (loader-loading-flag-leak): defensa para errores antes del MISS branch.
    store.dispatch(loadingData(tableId, false));
    return { totalCount: 0, campaigns: [], filters: { concessions: [], teams: [], users: [] }, filtersData: { data: { predefinedOrders: [] } }, pageInfo: { hasNextPage: false, hasPreviousPage: false }, roleActive: userData?.user?.user?.rolActive };
  }
};

  return { data: load() };
};
